/**
 * prerenderUtils.js
 * Utilities for handling data fetching and state during prerendering
 */

/**
 * Detects if the code is running in a prerendering environment
 * @returns {boolean} True if in prerendering environment
 */
export const isPrerendering = () => {
  return typeof navigator !== 'undefined' && 
    (navigator.userAgent === 'ReactSnap' || 
     navigator.userAgent.includes('Prerender') ||
     window.__PRERENDER_INJECTED ||
     window.prerenderReady === false);
};

/**
 * Helper to safely access window in both browser and prerender environments
 */
export const safeWindow = typeof window !== 'undefined' ? window : {};

/**
 * Marks prerendering as complete, which signals to tools like react-snap
 * that the page is ready to be captured
 */
export const markPrerenderReady = () => {
  if (typeof window !== 'undefined') {
    // Signal prerender readiness
    window.prerenderReady = true;
    
    // Add marker for PrerenderSPAPlugin
    if (!document.getElementById('content-fully-loaded')) {
      const marker = document.createElement('div');
      marker.id = 'content-fully-loaded';
      marker.style.display = 'none';
      document.body.appendChild(marker);
    }
    
    // Dispatch render event
    document.dispatchEvent(new Event('render-event'));
  }
};

/**
 * Wait for some time in prerender environment, useful for async data loading
 * @param {number} ms Milliseconds to wait
 * @returns {Promise} Promise that resolves after the specified time
 */
export const prerenderWait = (ms = 500) => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
};

/**
 * Wraps API calls to handle prerendering environment differently
 * @param {Function} apiCall The function that makes the API call
 * @param {any} fallbackData The data to use if in prerendering environment
 * @returns {Promise} Promise that resolves with API data or fallback data
 */
export const withPrerenderFallback = async (apiCall, fallbackData = null) => {
  if (isPrerendering()) {
    // In prerender environment, wait a bit then return fallback data
    await prerenderWait(100);
    return fallbackData;
  }
  
  // In browser environment, make the actual API call
  return apiCall();
};

/**
 * Clear old prerender cache entries to free up space
 * Preserves non-prerender data like auth tokens
 */
const clearOldPrerenderCache = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const keysToRemove = [];
    const now = Date.now();
    const maxAge = 3600000; // 1 hour
    
    // Find all prerender cache entries
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('prerender_')) {
        try {
          const item = JSON.parse(window.localStorage.getItem(key));
          // Remove if older than maxAge or if we need to clear space
          if (!item.timestamp || now - item.timestamp > maxAge) {
            keysToRemove.push(key);
          }
        } catch {
          // Invalid JSON, remove it
          keysToRemove.push(key);
        }
      }
    }
    
    // Remove old entries
    keysToRemove.forEach(key => {
      try {
        window.localStorage.removeItem(key);
      } catch (e) {
        // Ignore removal errors
      }
    });
    
    return keysToRemove.length;
  }
  return 0;
};

/**
 * Cache prerendered data in local storage for later hydration
 * @param {string} key The cache key
 * @param {any} data The data to cache
 */
export const cachePrerenderData = (key, data) => {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(
        `prerender_${key}`, 
        JSON.stringify({ data, timestamp: Date.now() })
      );
    } catch (error) {
      // Handle QuotaExceededError by clearing old cache
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        console.log('[PrerenderCache] Quota exceeded, clearing old cache entries...');
        const cleared = clearOldPrerenderCache();
        console.log(`[PrerenderCache] Cleared ${cleared} old entries`);
        
        // Try once more after clearing
        try {
          window.localStorage.setItem(
            `prerender_${key}`, 
            JSON.stringify({ data, timestamp: Date.now() })
          );
        } catch (retryError) {
          // Still failed, skip caching this item silently
          // Don't spam console with errors
        }
      }
    }
  }
};

/**
 * Retrieve cached prerendered data
 * @param {string} key The cache key
 * @param {number} maxAge Maximum age in milliseconds
 * @returns {any} The cached data or null if not found/expired
 */
export const getPrerenderData = (key, maxAge = 3600000) => {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const item = window.localStorage.getItem(`prerender_${key}`);
      if (!item) return null;
      
      const { data, timestamp } = JSON.parse(item);
      const now = Date.now();
      
      if (now - timestamp > maxAge) {
        window.localStorage.removeItem(`prerender_${key}`);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Failed to retrieve prerendered data:', error);
      return null;
    }
  }
  return null;
};